import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { HUB_FETCH_INTERVAL, HubsService } from '../../../../../core/services/hubs.service';
import { Hub, HubId, HubType } from '../../../../../shared/models/hub';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../app/app.state';
import { Subscription } from 'rxjs';
import { SortTable, SortTableRow } from '../../../../../shared/models/sort-table/types';
import { SearchService } from '../../../../../core/services/search.service';
import {
  SortTableCellKebabMenu,
  SortTableCellStatusDot,
  SortTableCellTextPill,
  SortTableCellTitleSharingInfo,
  SortTableStatusDotColor,
} from '../../../../../shared/models/sort-table/cell-classes';
import { HubStatus } from '../../../../../shared/models/hub-status';
import { SessionService } from '../../../../../core/services/session.service';
import { BaseTableViewComponent } from '../../../../components/_base-table-view/base-table-view.component';
import { URL_PARAM_KEY_FILTERS, UrlService } from '../../../../../core/services/url.service';
import { SubtitleItem } from '../../../../../core/state/title.state';
import { TitleChangedAction } from '../../../../../core/actions/title.actions';
import { ObjectModel, ObjectType } from '../../../../../shared/models/object.model';
import { SpinnerComponent } from '../../../../components/spinner/spinner.component';
import { SortTableComponent } from '../../../../components/sort-table/sort-table.component';
import { HubEditorComponent } from '../../../../components/hub-editor/hub-editor.component';
import { PermissionsService } from '../../../../../core/services/permissions.service';
import { Permission } from '../../../../../shared/models/fga/permission';
import { EMPTY_ID } from '../../../../../shared/constants/empty-id';
import { TableMenuOptionId } from '../../../../../shared/constants/menu-option-ids';
import { MotiChatService } from '../../../../../moti/services/moti-chat.service';
import { Filters } from '../../../../../shared/models/filter';
import { Option } from '../../../../../shared/models/option';

const COL_IDX_KEBAB = 0;
const COL_IDX_NAME = 1;
const COL_IDX_ID = 2;
const COL_IDX_STATUS = 3;
const COL_IDX_IP = 4;
const COL_IDX_VERSION = 5;
const COL_IDX_TIME_ZONE = 6;

@Component({
  selector: 'motn-hubs-existing',
  templateUrl: './hubs-existing.component.html',
  styleUrls: ['./hubs-existing.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AsyncPipe,
    SpinnerComponent,
    SortTableComponent,
    HubEditorComponent,
  ],
})
export class HubsExistingComponent extends BaseTableViewComponent implements OnInit, OnDestroy {
  hubs: Hub[] | undefined;
  searchTerm: string = '';
  editingHub: HubId = EMPTY_ID;
  renderedHubIds: HubId[];

  private hubStatusDots: { [hubId: HubId]: SortTableCellStatusDot } = {};
  private hubVersions: { [hubId: HubId]: SortTableCellTextPill } = {};
  private hubFetchTimeouts: { [hubId: HubId]: number } = {};

  private hubStatusSubs: { [hubId: HubId]: Subscription } = {};
  private searchSub: Subscription;


  constructor(
    private store: Store<AppState>,
    private hubsService: HubsService,
    private searchService: SearchService,
    private urlService: UrlService,
    private session: SessionService,
    private permissionsService: PermissionsService,
    private motiChatService: MotiChatService,
  ) {
    super();
  }


  ngOnInit(): void {
    this.searchService.enableSearch();

    this.searchSub = this.urlService.searchTerm$.subscribe((term) => {
      this.searchTerm = term;
      this.updateTable();
    });

    this.hubsService.fetchUserHubs$().subscribe(
      (hubs) => {
        this.mergeCachedHubs(hubs);
        this.loading = false;
      },
    );
  }


  ngOnDestroy() {
    this.searchSub?.unsubscribe();
    this.unsubAllHubStatuses();
  }


  protected updateTitle() {
    const subtitle: SubtitleItem[] = [];

    if (this.searchTerm) {
      subtitle.push(
        {
          icon: `magnifying-glass`,
          text: `Search term entered`,
        },
      );
    }

    if (this.searchTerm) {
      subtitle.push(
        {
          text: `${this.table$.getValue().rows.length.toLocaleString()} results`,
        },
      );
    } else {
      subtitle.push(
        {
          text: `${this.table$.getValue().rows.length.toLocaleString()} total`,
        },
      );
    }

    this.store.dispatch(new TitleChangedAction(
      '',
      'Hubs',
      subtitle,
    ));
  }


  /**
   * Takes an array of Hub objects and merges them into this.hubs
   *
   * @param hubs
   * @private
   */
  private mergeCachedHubs(hubs: Hub[]) {
    if (!this.hubs) {
      this.hubs = [...hubs];
    }

    for (let hub of hubs) {
      if (this.hubs !== hubs) {
        const knownIndex: number = this.hubs.findIndex(h => h.ID === hub.ID);

        if (knownIndex < 0) {
          this.hubs.push(hub);
        } else {
          this.hubs[knownIndex] = hub;
        }
      }
    }

    this.updateTable();
  }


  private unsubAllHubStatuses() {
    for (let hubId in this.hubStatusSubs) {
      this.hubsService.cancelFetchHubStatus(hubId);
      this.hubStatusSubs[hubId]?.unsubscribe();
      delete this.hubStatusSubs[hubId];
    }

    for (let t in this.hubFetchTimeouts) {
      clearTimeout(this.hubFetchTimeouts[t]);
      delete this.hubFetchTimeouts[t];
    }
  }


  tableRowsRendered(rows: SortTableRow[]) {
    this.unsubAllHubStatuses();

    const hubIds: HubId[] = [];

    for (let row of rows) {
      hubIds.push(row.context);
    }

    this.renderedHubIds = hubIds;
    this.fetchRenderedHubsStatuses();
  }


  editorSaved(hub: Hub) {
    this.mergeCachedHubs([hub]);
  }


  private fetchRenderedHubsStatuses() {
    for (let hubId of this.renderedHubIds) {
      this.fetchHubStatus(hubId);
    }
  }


  private fetchHubStatus(hubId: HubId) {
    clearTimeout(this.hubFetchTimeouts[hubId]);
    delete this.hubFetchTimeouts[hubId];

    const hub = this.hubs?.find(h => h.ID === hubId);

    this.hubStatusSubs[hubId] = this.hubsService.fetchHubStatus$(hubId).subscribe({
      next: (status) => {
        this.hubStatusDots[hubId].color = this.getStatusColor(status);
        this.hubStatusDots[hubId].label = this.getStatusLabel(status);
        this.hubVersions[hubId].mainText = this.getVersionDisplay(hub, status);
      },
      error: () => {
        this.hubStatusDots[hubId].color = 'yellow';
        this.hubStatusDots[hubId].label = 'Limited Connectivity';

        this.hubFetchTimeouts[hubId] = window.setTimeout(
          () => {
            this.fetchHubStatus(hubId);
          },
          HUB_FETCH_INTERVAL,
        );
      },
      complete: () => {
        this.hubFetchTimeouts[hubId] = window.setTimeout(
          () => {
            this.fetchHubStatus(hubId);
          },
          HUB_FETCH_INTERVAL,
        );
      },
    });
  }


  private getStatusColor(status: HubStatus): SortTableStatusDotColor {
    if (status.DownloadingUpdate) {
      return 'yellow';
    } else if (status.InstallingUpdate) {
      return 'yellow';
    } else if (status.Success) {
      return 'green';
    }

    return 'gray';
  }


  private getStatusLabel(status: HubStatus): string {
    if (status.DownloadingUpdate) {
      return 'Downloading Update';
    } else if (status.InstallingUpdate) {
      return 'Installing Update';
    } else if (status.Success) {
      return 'Connected';
    }

    return 'Not Connected';
  }


  private getSoftwareVersion(status: HubStatus): string {
    const ver = status.Response?.Version?.match(/\d+\.\d+\.\d+/g);

    if (ver?.length === 1) {
      return ver[0];
    }

    return '';
  }

  private getVersionDisplay(hub: Hub | undefined, status: HubStatus): string {
    if (hub?.Type === HubType.Cloud) {
      return 'Cloud';
    }

    const version = this.getSoftwareVersion(status);
    if (version) {
      return `On-Prem (${version})`;
    }

    return 'On-Prem';
  }


  protected updateTable() {
    if (!this.hubs) {
      return;
    }

    const table: SortTable = {
      css: {
        'min-width': '890px',
      },
      columns: [],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: 15,
        startOnPage: 1,
        maxPageOptions: 10,
      },
    };

    {
      table.columns[COL_IDX_NAME] = {
        name: 'Hub Name',
        css: {
          'width': 'calc(100% - 710px)',
        },
        sortingFn: SortTableCellTitleSharingInfo.sortingFnTitle,
      };

      table.columns[COL_IDX_ID] = {
        name: 'ID',
        css: {
          'width': '100px',
          'justify-content': 'center',
        },
        sortingFn: SortTableCellTextPill.sortingFnPillText,
        sortDefault: 'down',
      };

      table.columns[COL_IDX_STATUS] = {
        name: 'Status',
        css: {
          'width': '170px',
        },
      };

      table.columns[COL_IDX_IP] = {
        name: 'Location',
        css: {
          'width': '130px',
        },
        sortingFn: SortTableCellTextPill.sortingFnMainText,
      };

      table.columns[COL_IDX_VERSION] = {
        name: 'Version',
        css: {
          'width': '115px',
          'justify-content': 'center',
        },
      };

      table.columns[COL_IDX_TIME_ZONE] = {
        name: 'Time Zone',
        css: {
          'width': '155px',
          'justify-content': 'end',
        },
        sortingFn: SortTableCellTextPill.sortingFnMainText,
      };

      table.columns[COL_IDX_KEBAB] = {
        name: '',
        css: {
          'width': '40px',
        },
      };
    }

    let hubs: Hub[] = this.hubs;

    if (this.searchTerm) {
      hubs = this.searchService.searchHubs(this.searchTerm, hubs);
    }

    for (let hub of hubs) {
      let row: SortTableRow = this.tableRows[hub.ID];

      if (!row) {
        row = this.tableRows[hub.ID] = {
          context: '' + hub.ID,
          cells: [],
        };
      }

      this.upsertRowData(row, hub);
      table.rows.push(row);
    }

    this.searchService.setResultQty(table.rows.length);
    this.table$.next(table);
    this.updateTitle();
  }


  /**
   * For inserting data into a brand new table row or updating an existing
   * table row.
   *
   * @param row
   * @param hub
   * @private
   */
  protected upsertRowData(row: SortTableRow, hub: Hub) {

    let statusDot: SortTableCellStatusDot;
    if (!this.hubStatusDots[hub.ID]) {
      statusDot = this.hubStatusDots[hub.ID] = new SortTableCellStatusDot('gray', '');
    } else {
      statusDot = this.hubStatusDots[hub.ID];
    }
    let version: SortTableCellTextPill;
    if (!this.hubVersions[hub.ID]) {
      version = this.hubVersions[hub.ID] = new SortTableCellTextPill('', '', false);
    } else {
      version = this.hubVersions[hub.ID];
    }
    row.cells[COL_IDX_NAME] = new SortTableCellTitleSharingInfo({
      title: hub.Name,
      object: new ObjectModel({
        id: hub.ID,
        type: ObjectType.HUB,
        name: hub.Name,
        owner: hub.Owner,
      }),
    });
    row.cells[COL_IDX_ID] = new SortTableCellTextPill('', '' + hub.ID).setPillLetter('h');
    row.cells[COL_IDX_STATUS] = statusDot;
    row.cells[COL_IDX_IP] = new SortTableCellTextPill(
      hub.Type === HubType.Cloud ? 'Cloud' : hub.PublicIP,
      '',
      true
    );
    row.cells[COL_IDX_VERSION] = version;
    row.cells[COL_IDX_TIME_ZONE] = new SortTableCellTextPill(hub.TimeZone, '');
    // Max 3 options: View Associated Devices, Configure, Chat
    const kebabCell = new SortTableCellKebabMenu(hub.ID, [], hub.Name, false, 3);

    // Add click callback that will be called when kebab menu is opened
    kebabCell.addClickCallback(() => {
      this.loadKebabOptions(kebabCell, hub);
    });

    row.cells[COL_IDX_KEBAB] = kebabCell;
  }

  private getViewDevicesOption(hub: Hub): Option {
    const filters: Filters = {
      hubs: [hub.ID],
    };
    const viewDevicesParams = {};
    viewDevicesParams[URL_PARAM_KEY_FILTERS] = JSON.stringify(filters);

    return {
      id: TableMenuOptionId.VIEW_DEVICES,
      name: 'View Associated Devices',
      href: UrlService.controlsUrls.devicesExisting,
      params: viewDevicesParams,
    };
  }

  /**
   * Load kebab menu options by checking permissions
   */
  private loadKebabOptions(kebabCell: SortTableCellKebabMenu, hub: Hub): void {
    if (kebabCell.isLoading) return;

    kebabCell.isLoading = true;

    this.permissionsService.queryPermissions({
      UserID: this.session.userId,
      ObjectID: hub.ID,
      ObjectType: ObjectType.HUB,
      Permissions: [Permission.CAN_CONFIGURE, Permission.CAN_CHAT]
    }).subscribe({
      next: (response) => {
        const kebabOptions: any[] = [];

        // View Associated Devices option (always available)
        kebabOptions.push(this.getViewDevicesOption(hub));

        if (response.Results.find(r => r.Permission === Permission.CAN_CONFIGURE)?.Allowed) {
          kebabOptions.push(this.getConfigureOption(hub));
        }

        // Chat option
        if (response.Results.find(r => r.Permission === Permission.CAN_CHAT)?.Allowed) {
          kebabOptions.push({
            id: TableMenuOptionId.CHAT,
            name: 'Chat',
            icon: 'fa-kit fa-chat',
            callback: () => {
              this.motiChatService.openChat(hub.ID, hub.Name, ObjectType.HUB);
            },
          });
        }

        kebabCell.options = kebabOptions;
        kebabCell.isLoading = false;
      },
      error: () => {
        kebabCell.isLoading = false;
      },
    });
  }

  private getConfigureOption(hub: Hub) {
    return {
      id: TableMenuOptionId.CONFIGURE,
      name: 'Configure Hub',
      callback: () => {
        this.editingHub = hub.ID;
      },
    };
  }
}
